// Encrypted on-device storage (AES-GCM, non-extractable key in IndexedDB) for offline PHI.
const DB_NAME = "cw-secure";
const ITEMS = "items";
const KEYS = "keys";

const openDb = () => new Promise((resolve, reject) => {
  const req = indexedDB.open(DB_NAME, 1);
  req.onupgradeneeded = () => {
    req.result.createObjectStore(ITEMS);
    req.result.createObjectStore(KEYS);
  };
  req.onsuccess = () => resolve(req.result);
  req.onerror = () => reject(req.error);
});

const run = (db, store, mode, fn) => new Promise((resolve, reject) => {
  const t = db.transaction(store, mode);
  const req = fn(t.objectStore(store));
  t.oncomplete = () => resolve(req?.result);
  t.onerror = () => reject(t.error);
});

const getKey = async (db) => {
  let key = await run(db, KEYS, "readonly", (s) => s.get("k"));
  if (!key) {
    key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
    await run(db, KEYS, "readwrite", (s) => s.put(key, "k"));
  }
  return key;
};

export const secureSet = async (id, value) => {
  const db = await openDb();
  const key = await getKey(db);
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const data = await crypto.subtle.encrypt({ name: "AES-GCM", iv }, key, new TextEncoder().encode(JSON.stringify(value)));
  await run(db, ITEMS, "readwrite", (s) => s.put({ iv, data }, id));
};

const decrypt = async (key, rec) => JSON.parse(new TextDecoder().decode(await crypto.subtle.decrypt({ name: "AES-GCM", iv: rec.iv }, key, rec.data)));

export const secureGet = async (id) => {
  const db = await openDb();
  const rec = await run(db, ITEMS, "readonly", (s) => s.get(id));
  return rec ? decrypt(await getKey(db), rec) : null;
};

export const secureList = async (prefix) => {
  const db = await openDb();
  const key = await getKey(db);
  const ids = await run(db, ITEMS, "readonly", (s) => s.getAllKeys());
  const matching = ids.filter((k) => String(k).startsWith(prefix));
  return Promise.all(matching.map(async (k) => decrypt(key, await run(db, ITEMS, "readonly", (s) => s.get(k)))));
};

export const secureDelete = async (id) => {
  const db = await openDb();
  await run(db, ITEMS, "readwrite", (s) => s.delete(id));
};

export const secureClear = () => new Promise((resolve) => {
  const req = indexedDB.deleteDatabase(DB_NAME);
  req.onsuccess = req.onerror = req.onblocked = () => resolve();
});
